import React, {
  memo, useMemo, useRef, useState
} from "react";

// region External libraries
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Pagination,
  Tooltip
} from "@libraries/mui/components";
// endregion External libraries
// region Atoms
import SearchField from "@atoms/SearchField";
// endregion Atoms
// region Languages
import { GlobalMessages } from "@languages/interfaces";
import useTranslation, { TFunction } from "@languages/useTranslation";
// endregion Languages

import { MODULE_PRESENTATION } from "@pages/Launcher/catalog";
import { CsvColumn, downloadCsv, toFileNameSlug } from "src/helpers/downloadCsv";

import { LegacyMigrationMessages } from "../languages/interfaces";
import { translateReason } from "../messages";
import { LegacyMigrationCandidate, LegacyMigrationExtraction } from "../service";
import * as Styled from "../styles";

/** Same accent the launcher falls back to for a product with no presentation. */
const FALLBACK_ACCENT = "#2968A8";

/** Past a screenful of users, the list gets its search and quick filters. */
const LONG_LIST = 10;

/** Users per page — the page size of the announcement lists. Fifty repaint at once; seven hundred freeze the screen. */
const PAGE_SIZE = 50;

/** The element that scrolls the page: where "the top of the list is out of sight" is measured. */
const scrollParent = (element: HTMLElement): HTMLElement | null => {
  for (let node = element.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);

    if (overflowY === "auto" || overflowY === "scroll") return node;
  }

  return null;
};

/**
 * Quick filters over the eligible users, in the order they are shown. "Só CRM"
 * and "Usam outras áreas" split the list by where the lock starts; "Já no
 * Identity" shows who a previous run (or someone by hand) already created.
 */
type TFilter = "all" | "onlyCrm" | "otherAreas" | "inIdentity" | "leftOut";

const FILTER_LABEL: Record<TFilter, LegacyMigrationMessages> = {
  all: LegacyMigrationMessages.legacyMigrationFilterAll,
  onlyCrm: LegacyMigrationMessages.legacyMigrationFilterOnlyCrm,
  otherAreas: LegacyMigrationMessages.legacyMigrationFilterOtherAreas,
  inIdentity: LegacyMigrationMessages.legacyMigrationFilterInIdentity,
  leftOut: LegacyMigrationMessages.legacyMigrationFilterLeftOut
};

/** The legacy database answers in id order, which means nothing to the operator. */
const byName = (a: LegacyMigrationCandidate, b: LegacyMigrationCandidate) => a.name
  .localeCompare(b.name, undefined, { sensitivity: "base" });

interface ICandidateRowProps {
  candidate: LegacyMigrationCandidate;
  /** Modules the customer contracted; any other is shown faded. */
  contracted: string[];
  selected: boolean;
  lock: boolean;
  busy: boolean;
  t: TFunction;
  onToggle: (legacyId: string, selected: boolean) => void;
  onToggleLock: (legacyId: string, lock: boolean) => void;
}

/**
 * The password column: what happens to this user's password in Identity.
 * Someone already in Identity keeps their account as it is, so no legacy
 * password is carried over and no e-mail goes out.
 */
const PasswordBadge: React.FC<{ candidate: LegacyMigrationCandidate; t: TFunction }> = ({ candidate, t }) => {
  if (candidate.inIdentity) {
    return (
      <Styled.Badge $tone="info" title={String(t(LegacyMigrationMessages.legacyMigrationInIdentityHint))}>
        {t(LegacyMigrationMessages.legacyMigrationInIdentity)}
      </Styled.Badge>
    );
  }

  // A locked password is the random one Identity wrote: there is nothing to keep.
  if (candidate.legacyLocked) return <>—</>;

  if (candidate.passwordKept) {
    return (
      <Styled.Badge $tone="ok" title={String(t(LegacyMigrationMessages.legacyMigrationPasswordKeptHint))}>
        {t(LegacyMigrationMessages.legacyMigrationPasswordKept)}
      </Styled.Badge>
    );
  }

  // A legacy password B2C would refuse is not sent: the hover says why this user gets the e-mail.
  return (
    <Styled.Badge
      $tone="muted"
      title={candidate.hasPassword ? String(t(LegacyMigrationMessages.legacyMigrationPasswordWeakHint)) : undefined}
    >
      {t(LegacyMigrationMessages.legacyMigrationPasswordReset)}
    </Styled.Badge>
  );
};

/**
 * The lock needs a password the user can sign in to Identity with: the legacy
 * one, kept, or the one they already have there. Otherwise locking would leave
 * them out of both systems until the password e-mail — which may never arrive.
 */
const canLock = (candidate: LegacyMigrationCandidate): boolean => candidate.inIdentity || candidate.passwordKept;

/**
 * The "Situação" of the exported list: the password column, worded for the
 * customer. A password that will not be kept is something they can still fix —
 * a new one within the rules, set in the Topcon Legado before the migration.
 */
const exportStatus = (candidate: LegacyMigrationCandidate, t: TFunction): string => {
  if (candidate.inIdentity) return t(LegacyMigrationMessages.legacyMigrationInIdentity);
  if (candidate.legacyLocked) return t(LegacyMigrationMessages.legacyMigrationAlreadyLocked);
  if (candidate.passwordKept) return t(LegacyMigrationMessages.legacyMigrationPasswordKept);

  return t(LegacyMigrationMessages.legacyMigrationExportListNewLegacyPassword);
};

/**
 * One user of the review list.
 *
 * Memoised, and its props are all stable or primitive: a customer can bring
 * hundreds of users, and a click has to repaint its own row, not the table.
 * For the same reason the checkboxes skip the ripple, which mounts a second
 * render per checkbox.
 */
const CandidateRowBase: React.FC<ICandidateRowProps> = ({
  candidate,
  contracted,
  selected,
  lock,
  busy,
  t,
  onToggle,
  onToggleLock
}) => (
  <tr>
    <td>
      <Checkbox
        size="small"
        disableRipple
        checked={selected}
        disabled={busy}
        inputProps={{ "aria-label": candidate.name }}
        onChange={(event) => onToggle(candidate.legacyId, event.target.checked)}
      />
    </td>
    <td>
      <Styled.UserCell>
        <Styled.UserName>{candidate.name}</Styled.UserName>
        <Styled.UserEmail>{candidate.email}</Styled.UserEmail>
      </Styled.UserCell>
    </td>
    <td>
      <PasswordBadge candidate={candidate} t={t} />
    </td>
    <td>
      <Styled.Chips>
        {candidate.modules.map((module) => {
          const isContracted = contracted.includes(module);
          const presentation = MODULE_PRESENTATION[module];

          const chip = (
            <Chip
              key={module}
              size="small"
              label={presentation?.label ?? module}
              style={{
                backgroundColor: presentation?.accent ?? FALLBACK_ACCENT,
                color: "#fff",

                // A module the customer has not contracted keeps its colour — the
                // operator recognises the product by it — but faded and outlined:
                // it will not be granted.
                opacity: isContracted ? 1 : 0.55,
                border: isContracted ? undefined : "1px dashed #8a5a00"
              }}
            />
          );

          // Only the exception has something to say on hover.
          return isContracted ? chip : (
            <Tooltip key={module} title={String(t(LegacyMigrationMessages.legacyMigrationNotContracted))}>
              {chip}
            </Tooltip>
          );
        })}
      </Styled.Chips>
    </td>
    <td>
      {candidate.legacyLocked ? (
        <Styled.Badge $tone="muted">{t(LegacyMigrationMessages.legacyMigrationAlreadyLocked)}</Styled.Badge>
      ) : (
        <Styled.LegacyCell>
          <FormControlLabel
            label={String(t(LegacyMigrationMessages.legacyMigrationLock))}
            title={canLock(candidate) ? undefined : String(t(LegacyMigrationMessages.legacyMigrationLockNeedsKeptPassword))}
            control={(
              <Checkbox
                size="small"
                disableRipple
                checked={lock && canLock(candidate)}
                disabled={busy || !selected || !canLock(candidate)}
                onChange={(event) => onToggleLock(candidate.legacyId, event.target.checked)}
              />
            )}
          />
          {/* Other legacy areas are why the lock starts unticked: that user
              probably still works in the Topcon Legado. */}
          {candidate.otherLegacyAreas.length > 0 && (
            <Styled.LegacyAreas>
              {t(LegacyMigrationMessages.legacyMigrationAlsoUses, { areas: candidate.otherLegacyAreas.join(", ") })}
            </Styled.LegacyAreas>
          )}
        </Styled.LegacyCell>
      )}
    </td>
  </tr>
);

const CandidateRow = memo(CandidateRowBase);

interface IReviewProps {
  extraction: LegacyMigrationExtraction;
  /** Legacy ids the operator wants to migrate. */
  selected: Set<string>;
  /** Legacy ids whose Topcon Legado password will be locked. */
  lockLegacy: Set<string>;
  busy: boolean;
  onToggle: (legacyId: string, selected: boolean) => void;
  onToggleMany: (legacyIds: string[], selected: boolean) => void;
  onToggleLock: (legacyId: string, lock: boolean) => void;
  onToggleLockMany: (legacyIds: string[], lock: boolean) => void;
  onConfirm: () => void;
  onBack: () => void;
}

/**
 * The review — the only step that asks the operator anything: who gets migrated.
 *
 * Everyone eligible and not yet in Identity arrives selected, so the common
 * answer is "yes, all of them" and the work is unselecting exceptions. Users
 * that cannot be migrated are kept out of that decision, in a collapsed block
 * with their reason.
 *
 * Built for customers with hundreds of users: the list is sorted by name,
 * narrowed by search and quick filters, and shown a page at a time. The bulk
 * actions act on the whole filtered result, across its pages.
 */
const Review: React.FC<IReviewProps> = ({
  extraction,
  selected,
  lockLegacy,
  busy,
  onToggle,
  onToggleMany,
  onToggleLock,
  onToggleLockMany,
  onConfirm,
  onBack
}) => {

  // region Hooks
  const { t } = useTranslation();
  // endregion Hooks
  // region States
  const [search, setSearch] = useState<string>("");
  const [filter, setFilter] = useState<TFilter>("all");
  /** Who was left out when that filter was picked: ticking someone back must not make the row vanish under the cursor. */
  const [leftOutWhenPicked, setLeftOutWhenPicked] = useState<Set<string>>(new Set());
  const [page, setPage] = useState<number>(1);
  // endregion States
  // region Refs
  /** Top of the list: where a new page starts reading. */
  const listTop = useRef<HTMLDivElement>(null);
  // endregion Refs

  const contractedModules = useMemo(
    () => extraction.contractedModules.filter((module) => module.contracted).map((module) => module.module),
    [extraction.contractedModules]
  );

  const eligible = useMemo(
    () => extraction.candidates.filter((candidate) => candidate.eligible).sort(byName),
    [extraction.candidates]
  );

  const ineligible = useMemo(
    () => extraction.candidates.filter((candidate) => !candidate.eligible).sort(byName),
    [extraction.candidates]
  );

  const otherAreasCount = useMemo(
    () => eligible.filter((candidate) => candidate.otherLegacyAreas.length > 0).length,
    [eligible]
  );

  /** Modules of the eligible users the customer has not contracted: said before anyone is migrated. */
  const uncontracted = useMemo(
    () => Array.from(new Set(eligible.flatMap((candidate) => candidate.modules)))
      .filter((module) => !contractedModules.includes(module)),
    [contractedModules, eligible]
  );

  /** Selected users who would get at least one contracted module. With none, migrating would only skip people. */
  const migratable = useMemo(
    () => eligible.filter((candidate) => selected.has(candidate.legacyId)
      && candidate.modules.some((module) => contractedModules.includes(module))).length,
    [contractedModules, eligible, selected]
  );

  const counts: Record<TFilter, number> = {
    all: eligible.length,
    onlyCrm: eligible.length - otherAreasCount,
    otherAreas: otherAreasCount,
    inIdentity: eligible.filter((candidate) => candidate.inIdentity).length,
    leftOut: eligible.filter((candidate) => !selected.has(candidate.legacyId)).length
  };

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();

    return eligible.filter((candidate) => {
      if (filter === "leftOut" && !leftOutWhenPicked.has(candidate.legacyId)) return false;
      if (filter === "onlyCrm" && candidate.otherLegacyAreas.length > 0) return false;
      if (filter === "otherAreas" && candidate.otherLegacyAreas.length === 0) return false;
      if (filter === "inIdentity" && !candidate.inIdentity) return false;

      return !term || candidate.email.toLowerCase().includes(term) || candidate.name.toLowerCase().includes(term);
    });
  }, [eligible, filter, leftOutWhenPicked, search]);

  const visibleIds = useMemo(() => visible.map((candidate) => candidate.legacyId), [visible]);

  /** A search or a filter is on: the bulk actions say they act on the filtered users, not on everyone. */
  const narrowed = filter !== "all" || search.trim() !== "";

  // Who the "Legado" header checkbox acts on: filtered, selected for migration,
  // not locked yet — on every page, like the bulk actions.
  const lockable = visible.filter((candidate) => !candidate.legacyLocked && selected.has(candidate.legacyId) && canLock(candidate));
  const lockedCount = lockable.filter((candidate) => lockLegacy.has(candidate.legacyId)).length;

  // A new search or filter starts again from the first page.
  const pickFilter = (next: TFilter) => {
    if (next === "leftOut") {
      setLeftOutWhenPicked(new Set(eligible
        .filter((candidate) => !selected.has(candidate.legacyId))
        .map((candidate) => candidate.legacyId)));
    }

    setFilter(next);
    setPage(1);
  };

  const applySearch = (term: string) => {
    setSearch(term);
    setPage(1);
  };

  const pageCount = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const rendered = visible.slice(pageStart, pageStart + PAGE_SIZE);

  /**
   * The list the customer checks before the migration: every eligible user,
   * whatever the selection, with one column per product for them to mark.
   * FLEET starts blank — the Topcon Legado only grants the CRM.
   */
  const exportList = () => {
    const yesNo = (value: boolean) => t(value ? GlobalMessages.yesText : GlobalMessages.noText);

    const columns: CsvColumn<LegacyMigrationCandidate>[] = [
      { header: t(LegacyMigrationMessages.legacyMigrationColumnUser), value: (row) => row.name },
      { header: t(LegacyMigrationMessages.legacyMigrationExportListEmail), value: (row) => row.email },
      { header: t(LegacyMigrationMessages.legacyMigrationExportListStatus), value: (row) => exportStatus(row, t) },
      { header: MODULE_PRESENTATION.TOPCON_CRM.label, value: (row) => yesNo(row.modules.includes("TOPCON_CRM")) },
      { header: MODULE_PRESENTATION.TOPCON_FLEET.label, value: () => "" },
      {
        header: t(LegacyMigrationMessages.legacyMigrationExportListLockLegacy),
        value: (row) => yesNo(row.legacyLocked || (lockLegacy.has(row.legacyId) && canLock(row)))
      }
    ];

    downloadCsv(`usuarios-${toFileNameSlug(extraction.tenantName)}.csv`, columns, eligible);
  };

  /** Changes page and, when the list was scrolled past its top, brings that top back into view. */
  const goToPage = (next: number) => {
    setPage(next);

    const element = listTop.current;
    const container = element && scrollParent(element);

    if (element && container && element.getBoundingClientRect().top < container.getBoundingClientRect().top) {
      element.scrollIntoView({ block: "start" });
    }
  };

  return (
    <Styled.Card>
      <Styled.StepTitle>
        {t(LegacyMigrationMessages.legacyMigrationReviewTitle, {
          count: selected.size,
          tenant: extraction.tenantName
        })}
      </Styled.StepTitle>
      <Styled.StepHint>
        {t(LegacyMigrationMessages.legacyMigrationReviewHint)}
        {" "}
        {t(LegacyMigrationMessages.legacyMigrationReviewLockHint)}
      </Styled.StepHint>

      {/* First thing on the step: without the module, the migration cannot give these users access. */}
      {uncontracted.length > 0 && (
        <Styled.Banner $tone="warning">
          {t(LegacyMigrationMessages.legacyMigrationNotContractedBanner, {
            tenant: extraction.tenantName,
            modules: uncontracted.map((module) => MODULE_PRESENTATION[module]?.label ?? module).join(", ")
          })}
        </Styled.Banner>
      )}

      {/* Above the list, not after it: past a few hundred users, the end of
          the page is somewhere nobody looks. */}
      {ineligible.length > 0 && (
        <Styled.Ineligible>
          <Accordion>
            <AccordionSummary>
              {t(LegacyMigrationMessages.legacyMigrationIneligibleCount, { count: ineligible.length })}
            </AccordionSummary>
            <AccordionDetails>
              <Styled.IneligibleScroll>
                <Styled.Table>
                  <tbody>
                    {ineligible.map((candidate) => (
                      <tr key={candidate.legacyId}>
                        <td>
                          <Styled.UserCell>
                            <Styled.UserName>{candidate.name}</Styled.UserName>
                            <Styled.UserEmail>{candidate.email || "—"}</Styled.UserEmail>
                          </Styled.UserCell>
                        </td>
                        <td>{translateReason(t, candidate.skipReason)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Styled.Table>
              </Styled.IneligibleScroll>
            </AccordionDetails>
          </Accordion>
        </Styled.Ineligible>
      )}

      <Styled.Toolbar>
        {eligible.length > LONG_LIST && (
          <>
            <Styled.SearchBox>
              <SearchField
                fieldLabel={t(LegacyMigrationMessages.legacyMigrationSearchUser)}
                onSearchClick={applySearch}
                onClearClick={() => applySearch("")}
              />
            </Styled.SearchBox>
            <Styled.Filters>
              {(Object.keys(FILTER_LABEL) as TFilter[]).map((key) => (
                <Styled.FilterChip key={key} type="button" $active={filter === key} onClick={() => pickFilter(key)}>
                  {t(FILTER_LABEL[key])}
                  <span>{counts[key]}</span>
                </Styled.FilterChip>
              ))}
            </Styled.Filters>
          </>
        )}

        {/* The exception, not the main action: out of the way, on the right. */}
        <Styled.SecondaryActions>
          <Button size="small" disabled={busy || visible.length === 0} onClick={() => onToggleMany(visibleIds, true)}>
            {narrowed
              ? t(LegacyMigrationMessages.legacyMigrationSelectShown, { count: visible.length })
              : t(LegacyMigrationMessages.legacyMigrationSelectAll)}
          </Button>
          <Button size="small" disabled={busy || visible.length === 0} onClick={() => onToggleMany(visibleIds, false)}>
            {narrowed
              ? t(LegacyMigrationMessages.legacyMigrationClearShown, { count: visible.length })
              : t(LegacyMigrationMessages.legacyMigrationClearAll)}
          </Button>
        </Styled.SecondaryActions>
      </Styled.Toolbar>

      {visible.length === 0 ? (
        <Styled.Empty>{t(LegacyMigrationMessages.legacyMigrationNoUsersFound)}</Styled.Empty>
      ) : (
        <Styled.TableScroll ref={listTop}>
          <Styled.Table $selectable>
            <thead>
              <tr>
                <th aria-label="select" />
                <th>{t(LegacyMigrationMessages.legacyMigrationColumnUser)}</th>
                <th>{t(LegacyMigrationMessages.legacyMigrationColumnPassword)}</th>
                <th>{t(LegacyMigrationMessages.legacyMigrationColumnModules)}</th>
                <th>
                  <Styled.LegacyHeader>
                    <Checkbox
                      size="small"
                      disableRipple
                      inputProps={{ "aria-label": String(t(LegacyMigrationMessages.legacyMigrationLockAll)) }}
                      checked={lockable.length > 0 && lockedCount === lockable.length}
                      indeterminate={lockedCount > 0 && lockedCount < lockable.length}
                      disabled={busy || lockable.length === 0}
                      onChange={(event) => onToggleLockMany(
                        lockable.map((candidate) => candidate.legacyId),
                        event.target.checked
                      )}
                    />
                    {t(LegacyMigrationMessages.legacyMigrationColumnLegacy)}
                  </Styled.LegacyHeader>
                </th>
              </tr>
            </thead>
            <tbody>
              {rendered.map((candidate) => (
                <CandidateRow
                  key={candidate.legacyId}
                  candidate={candidate}
                  contracted={contractedModules}
                  selected={selected.has(candidate.legacyId)}
                  lock={lockLegacy.has(candidate.legacyId)}
                  busy={busy}
                  t={t}
                  onToggle={onToggle}
                  onToggleLock={onToggleLock}
                />
              ))}
            </tbody>
          </Styled.Table>
        </Styled.TableScroll>
      )}

      {/* Where the reading of a page ends, which is where the next one is asked for. */}
      {pageCount > 1 && (
        <Styled.Pager>
          <span>
            {t(LegacyMigrationMessages.legacyMigrationPageRange, {
              from: pageStart + 1,
              to: pageStart + rendered.length,
              total: visible.length
            })}
          </span>
          <Pagination
            count={pageCount}
            page={currentPage}
            shape="rounded"
            onChange={(_event, next) => goToPage(next)}
            getItemAriaLabel={(type, item) => {
              if (type === "next") return String(t(LegacyMigrationMessages.legacyMigrationPageNext));
              if (type === "previous") return String(t(LegacyMigrationMessages.legacyMigrationPagePrevious));

              return String(t(LegacyMigrationMessages.legacyMigrationPageGoTo, { page: item }));
            }}
          />
        </Styled.Pager>
      )}

      <Styled.StickyActions>
        <Button disabled={busy} onClick={onBack}>
          {t(LegacyMigrationMessages.legacyMigrationBack)}
        </Button>
        <Button
          disabled={eligible.length === 0}
          title={String(t(LegacyMigrationMessages.legacyMigrationExportListHint))}
          onClick={exportList}
        >
          {t(LegacyMigrationMessages.legacyMigrationExportList)}
        </Button>
        <Styled.Spacer />
        <Button
          variant="contained"
          size="large"
          disabled={busy || migratable === 0}
          onClick={onConfirm}
        >
          {t(LegacyMigrationMessages.legacyMigrationConfirm, { count: selected.size })}
        </Button>
      </Styled.StickyActions>
    </Styled.Card>
  );
};

export default Review;
